/**
 * Browser-side application logic for the garden metrics report, returned as a plain string
 * so it can be spliced into a `<script>` tag in the generated static HTML file. This is NOT
 * type-checked TypeScript — it runs standalone in the report's own `<script>` tag against the
 * vendored Chart.js UMD global (`window.Chart`) and the inlined `window.__GARDEN_REPORT_DATA__`.
 *
 * Deliberately avoids template literals/backticks in the JS text below so this string can be
 * embedded inside this module's own TS template literal (and, downstream, inside the report's
 * outer HTML template literal) without any escaping gymnastics.
 */
export function reportClientScript(): string {
  return [
    "(function () {",
    '  "use strict";',
    "  var data = window.__GARDEN_REPORT_DATA__;",
    "  var state = { viewMode: 'steps', metric: 'time' };",
    "  var barMeta = []; // parallel to chart.data.labels; { kind: 'step'|'flower', ...}",
    "  var root = document.documentElement;",
    "  var style = getComputedStyle(root);",
    "  function cssVar(name) { return style.getPropertyValue(name).trim(); }",
    "",
    "  var SERIES_COLORS = [1, 2, 3, 4, 5, 6, 7, 8].map(function (n) { return cssVar('--series-' + n); });",
    "  var CRITICAL_COLOR = cssVar('--status-critical');",
    "  var GRIDLINE_COLOR = cssVar('--gridline-chart');",
    "  var TEXT_SECONDARY = cssVar('--text-secondary');",
    "",
    "  function flowerColor(flowerOrder) { return SERIES_COLORS[flowerOrder % SERIES_COLORS.length]; }",
    "",
    "  function hexToRgba(hex, alpha) {",
    "    var h = hex.replace('#', '');",
    "    var r = parseInt(h.substring(0, 2), 16);",
    "    var g = parseInt(h.substring(2, 4), 16);",
    "    var b = parseInt(h.substring(4, 6), 16);",
    "    return 'rgba(' + r + ',' + g + ',' + b + ',' + alpha + ')';",
    "  }",
    "",
    "  function formatDuration(ms) {",
    "    if (ms == null) return '—';",
    "    if (ms < 1000) return ms + 'ms';",
    "    var totalSeconds = ms / 1000;",
    "    if (totalSeconds < 60) return totalSeconds.toFixed(1) + 's';",
    "    var minutes = Math.floor(totalSeconds / 60);",
    "    var seconds = Math.round(totalSeconds % 60);",
    "    return minutes + 'm ' + seconds + 's';",
    "  }",
    "",
    "  function formatNumber(n) {",
    "    if (n == null) return '—';",
    "    return n.toLocaleString();",
    "  }",
    "",
    "  function formatCost(n) {",
    "    if (n == null) return '—';",
    "    return '$' + n.toFixed(4);",
    "  }",
    "",
    "  var THINKING_LEVELS = ['off', 'minimal', 'low', 'medium', 'high', 'xhigh'];",
    "",
    "  function thinkingIndex(level) {",
    "    if (level == null) return null;",
    "    var idx = THINKING_LEVELS.indexOf(level);",
    "    return idx === -1 ? null : idx;",
    "  }",
    "",
    "  function formatThinkingLevel(v) {",
    "    if (v == null) return '—';",
    "    var idx = Math.round(v);",
    "    if (idx < 0) idx = 0;",
    "    if (idx > THINKING_LEVELS.length - 1) idx = THINKING_LEVELS.length - 1;",
    "    return THINKING_LEVELS[idx];",
    "  }",
    "",
    "  // ---- Summary strip -------------------------------------------------",
    "  document.getElementById('summaryDuration').textContent = formatDuration(data.totals.durationMs);",
    "  document.getElementById('summaryTokens').textContent = formatNumber(data.totals.tokens.total);",
    "  document.getElementById('summaryCost').textContent = formatCost(data.totals.cost.total);",
    "  var errorEl = document.getElementById('summaryErrors');",
    "  errorEl.textContent = formatNumber(data.totals.errorCount);",
    "  if (data.totals.errorCount > 0) errorEl.classList.add('has-errors');",
    "",
    "  // ---- Table -------------------------------------------------------",
    "  var tbody = document.getElementById('stepsTableBody');",
    "  var sortHeaders = document.querySelectorAll('#stepsTable thead th[data-sort-key]');",
    "  var sortState = { key: 'globalOrder', direction: 'asc' };",
    "",
    "  function getSortValue(entry, key) {",
    "    switch (key) {",
    "      case 'globalOrder': return entry.metrics ? entry.globalOrder : entry.flowerOrder;",
    "      case 'durationMs': { var d = durationValue(entry); return d == null ? 0 : d; }",
    "      case 'toolCallCount':",
    "        return entry.metrics ? entry.metrics.toolCallCount : flowerToolCallCount(entry);",
    "      case 'errorCount': return entry.metrics ? entry.metrics.errorCount : entry.totals.errorCount;",
    "      case 'thinkingLevel': { var idx = thinkingValue(entry); return idx == null ? -1 : idx; }",
    "      case 'usage.input': return tokenValue(entry, 'input') || 0;",
    "      case 'usage.output': return tokenValue(entry, 'output') || 0;",
    "      case 'usage.cacheRead': return tokenValue(entry, 'cacheRead') || 0;",
    "      case 'usage.cacheWrite': return tokenValue(entry, 'cacheWrite') || 0;",
    "      case 'usage.totalTokens': return tokenValue(entry, 'totalTokens') || 0;",
    "      case 'usage.cost.input': return costValue(entry, 'input') || 0;",
    "      case 'usage.cost.output': return costValue(entry, 'output') || 0;",
    "      case 'usage.cost.cacheRead': return costValue(entry, 'cacheRead') || 0;",
    "      case 'usage.cost.cacheWrite': return costValue(entry, 'cacheWrite') || 0;",
    "      case 'usage.cost.total': return costValue(entry, 'total') || 0;",
    "      default: return 0;",
    "    }",
    "  }",
    "",
    "  function buildStepRow(step) {",
    "    var m = step.metrics;",
    "    var usage = m.usage;",
    "    var tr = document.createElement('tr');",
    "    tr.setAttribute('data-global-order', String(step.globalOrder));",
    "    tr.setAttribute('data-flower-name', step.flowerName);",
    "    tr.style.setProperty('--row-color', flowerColor(step.flowerOrder));",
    "    if (m.errorCount > 0) tr.classList.add('error-row');",
    "",
    "    var modelLabel = m.model && (m.model.provider || m.model.id)",
    "      ? [m.model.provider, m.model.id].filter(Boolean).join('/')",
    "      : '—';",
    "",
    "    var cells = [",
    "      String(step.globalOrder + 1),",
    "      step.flowerName,",
    "      step.stepLabel,",
    "      modelLabel,",
    "      m.thinkingLevel || '—',",
    "      formatDuration(m.durationMs),",
    "      usage ? formatNumber(usage.input) : '—',",
    "      usage ? formatNumber(usage.output) : '—',",
    "      usage ? formatNumber(usage.cacheRead) : '—',",
    "      usage ? formatNumber(usage.cacheWrite) : '—',",
    "      usage ? formatNumber(usage.totalTokens) : '—',",
    "      usage ? formatCost(usage.cost.input) : '—',",
    "      usage ? formatCost(usage.cost.output) : '—',",
    "      usage ? formatCost(usage.cost.cacheRead) : '—',",
    "      usage ? formatCost(usage.cost.cacheWrite) : '—',",
    "      usage ? formatCost(usage.cost.total) : '—',",
    "      String(m.toolCallCount),",
    "      m.toolNames && m.toolNames.length ? m.toolNames.join(', ') : '—',",
    "      String(m.errorCount),",
    "      m.lastErrorMessage || '—',",
    "    ];",
    "",
    "    cells.forEach(function (value, i) {",
    "      var td = document.createElement('td');",
    "      var isLast = i === cells.length - 1;",
    "      if (isLast) td.className = 'wrap';",
    "      td.textContent = value;",
    "      tr.appendChild(td);",
    "    });",
    "",
    "    return tr;",
    "  }",
    "",
    "  function flowerToolCallCount(flower) {",
    "    return data.steps",
    "      .filter(function (s) { return s.flowerName === flower.flowerName; })",
    "      .reduce(function (acc, s) { return acc + s.metrics.toolCallCount; }, 0);",
    "  }",
    "",
    "  function buildFlowerRow(flower) {",
    "    var tr = document.createElement('tr');",
    "    tr.setAttribute('data-flower-name', flower.flowerName);",
    "    tr.style.setProperty('--row-color', flowerColor(flower.flowerOrder));",
    "    if (flower.totals.errorCount > 0) tr.classList.add('error-row');",
    "",
    "    var cells = [",
    "      String(flower.flowerOrder + 1),",
    "      flower.flowerName,",
    "      '—',",
    "      '—',",
    "      '—',",
    "      formatDuration(durationValue(flower)),",
    "      formatNumber(tokenValue(flower, 'input')),",
    "      formatNumber(tokenValue(flower, 'output')),",
    "      formatNumber(tokenValue(flower, 'cacheRead')),",
    "      formatNumber(tokenValue(flower, 'cacheWrite')),",
    "      formatNumber(tokenValue(flower, 'totalTokens')),",
    "      formatCost(costValue(flower, 'input')),",
    "      formatCost(costValue(flower, 'output')),",
    "      formatCost(costValue(flower, 'cacheRead')),",
    "      formatCost(costValue(flower, 'cacheWrite')),",
    "      formatCost(costValue(flower, 'total')),",
    "      String(flowerToolCallCount(flower)),",
    "      '—',",
    "      String(flower.totals.errorCount),",
    "      '—',",
    "    ];",
    "",
    "    cells.forEach(function (value, i) {",
    "      var td = document.createElement('td');",
    "      var isLast = i === cells.length - 1;",
    "      if (isLast) td.className = 'wrap';",
    "      td.textContent = value;",
    "      tr.appendChild(td);",
    "    });",
    "",
    "    return tr;",
    "  }",
    "",
    "  function updateSortIndicators() {",
    "    sortHeaders.forEach(function (th) {",
    "      var key = th.getAttribute('data-sort-key');",
    "      if (key === sortState.key) {",
    "        th.classList.add('sort-active');",
    "        th.setAttribute('data-sort-arrow', sortState.direction === 'asc' ? '▲' : '▼');",
    "      } else {",
    "        th.classList.remove('sort-active');",
    "        th.removeAttribute('data-sort-arrow');",
    "      }",
    "    });",
    "  }",
    "",
    "  function renderTable() {",
    "    var mode = state.viewMode;",
    "    var entries = mode === 'steps' ? data.steps : data.flowers;",
    "    var buildRow = mode === 'steps' ? buildStepRow : buildFlowerRow;",
    "",
    "    var sorted = entries.slice().sort(function (a, b) {",
    "      var av = getSortValue(a, sortState.key);",
    "      var bv = getSortValue(b, sortState.key);",
    "      var cmp = av < bv ? -1 : (av > bv ? 1 : 0);",
    "      return sortState.direction === 'asc' ? cmp : -cmp;",
    "    });",
    "",
    "    tbody.innerHTML = '';",
    "",
    "    if (sorted.length === 0) {",
    "      var emptyRow = document.createElement('tr');",
    "      var emptyCell = document.createElement('td');",
    "      emptyCell.colSpan = 20;",
    "      emptyCell.className = 'empty-state';",
    "      emptyCell.textContent = mode === 'steps'",
    "        ? 'No steps recorded for this garden yet.'",
    "        : 'No workflows recorded for this garden yet.';",
    "      emptyRow.appendChild(emptyCell);",
    "      tbody.appendChild(emptyRow);",
    "    }",
    "",
    "    sorted.forEach(function (entry) {",
    "      tbody.appendChild(buildRow(entry));",
    "    });",
    "",
    "    updateSortIndicators();",
    "  }",
    "",
    "  sortHeaders.forEach(function (th) {",
    "    th.addEventListener('click', function () {",
    "      var key = th.getAttribute('data-sort-key');",
    "      if (sortState.key === key) {",
    "        sortState.direction = sortState.direction === 'asc' ? 'desc' : 'asc';",
    "      } else {",
    "        sortState.key = key;",
    "        sortState.direction = 'asc';",
    "      }",
    "      renderTable();",
    "    });",
    "  });",
    "",
    "  renderTable();",
    "",
    "  // ---- Chart ---------------------------------------------------------",
    "  var COMPONENT_ALPHAS = [1, 0.72, 0.5, 0.32];",
    "",
    "  function buildTimeSeries(entries, getFlowerOrder, getValue, getError, label) {",
    "    var colors = entries.map(function (e) { return flowerColor(getFlowerOrder(e)); });",
    "    return {",
    "      datasets: [{",
    "        label: label,",
    "        data: entries.map(getValue),",
    "        backgroundColor: colors,",
    "        borderColor: entries.map(function (e, i) { return getError(e) ? CRITICAL_COLOR : colors[i]; }),",
    "        borderWidth: entries.map(function (e) { return getError(e) ? 2 : 0; }),",
    "      }],",
    "    };",
    "  }",
    "",
    "  function buildStackedSeries(entries, getFlowerOrder, componentKeys, getComponentValue, getError) {",
    "    var datasets = componentKeys.map(function (key, componentIndex) {",
    "      return {",
    "        label: key.label,",
    "        stack: 'total',",
    "        data: entries.map(function (e) { return getComponentValue(e, key.field); }),",
    "        backgroundColor: entries.map(function (e) {",
    "          return hexToRgba(flowerColor(getFlowerOrder(e)), COMPONENT_ALPHAS[componentIndex]);",
    "        }),",
    "        borderColor: entries.map(function (e) { return getError(e) ? CRITICAL_COLOR : 'transparent'; }),",
    "        borderWidth: entries.map(function (e) { return getError(e) ? 2 : 0; }),",
    "      };",
    "    });",
    "    return { datasets: datasets };",
    "  }",
    "",
    "  var TOKEN_COMPONENTS = [",
    "    { field: 'input', label: 'Input tokens' },",
    "    { field: 'output', label: 'Output tokens' },",
    "    { field: 'cacheRead', label: 'Cache read tokens' },",
    "    { field: 'cacheWrite', label: 'Cache write tokens' },",
    "  ];",
    "  var COST_COMPONENTS = [",
    "    { field: 'input', label: 'Input cost' },",
    "    { field: 'output', label: 'Output cost' },",
    "    { field: 'cacheRead', label: 'Cache read cost' },",
    "    { field: 'cacheWrite', label: 'Cache write cost' },",
    "  ];",
    "",
    "  function tokenValue(entry, field) {",
    "    if (entry.metrics) return entry.metrics.usage ? entry.metrics.usage[field] : 0;",
    "    var totalsField = field === 'totalTokens' ? 'total' : field;",
    "    return entry.totals.tokens[totalsField];",
    "  }",
    "  function costValue(entry, field) {",
    "    if (entry.metrics) return entry.metrics.usage ? entry.metrics.usage.cost[field] : 0;",
    "    return entry.totals.cost[field];",
    "  }",
    "  function durationValue(entry) {",
    "    return entry.metrics ? entry.metrics.durationMs : entry.totals.durationMs;",
    "  }",
    "  function thinkingValue(entry) {",
    "    if (entry.metrics) return thinkingIndex(entry.metrics.thinkingLevel);",
    "    var steps = data.steps.filter(function (s) { return s.flowerName === entry.flowerName; });",
    "    var known = steps",
    "      .map(function (s) { return thinkingIndex(s.metrics.thinkingLevel); })",
    "      .filter(function (v) { return v !== null; });",
    "    if (!known.length) return null;",
    "    var sum = known.reduce(function (acc, v) { return acc + v; }, 0);",
    "    var rounded = Math.round(sum / known.length);",
    "    return Math.max(0, Math.min(THINKING_LEVELS.length - 1, rounded));",
    "  }",
    "  function errorFlag(entry) {",
    "    return entry.metrics ? entry.metrics.errorCount > 0 : entry.totals.errorCount > 0;",
    "  }",
    "  function flowerOrderOf(entry) {",
    "    return entry.flowerOrder;",
    "  }",
    "",
    "  function yAxisFormatter(metric) {",
    "    if (metric === 'time') return function (v) { return formatDuration(v); };",
    "    if (metric === 'tokens') return function (v) { return formatNumber(v); };",
    "    if (metric === 'thinking') return function (v) { return formatThinkingLevel(v); };",
    "    return function (v) { return formatCost(v); };",
    "  }",
    "",
    "  function buildChartConfig() {",
    "    var entries = state.viewMode === 'steps' ? data.steps : data.flowers;",
    "    var labels = entries.map(function (e) {",
    "      return state.viewMode === 'steps' ? e.stepLabel : e.flowerName;",
    "    });",
    "    barMeta = entries.map(function (e) {",
    "      var meta = state.viewMode === 'steps'",
    "        ? { kind: 'step', globalOrder: e.globalOrder, flowerName: e.flowerName, stepLabel: e.stepLabel }",
    "        : { kind: 'flower', flowerName: e.flowerName };",
    "      meta.tokensTotal = tokenValue(e, 'totalTokens');",
    "      meta.costTotal = costValue(e, 'total');",
    "      return meta;",
    "    });",
    "",
    "    var series;",
    "    if (state.metric === 'time') {",
    "      series = buildTimeSeries(entries, flowerOrderOf, durationValue, errorFlag, 'Duration');",
    "    } else if (state.metric === 'tokens') {",
    "      series = buildStackedSeries(entries, flowerOrderOf, TOKEN_COMPONENTS, tokenValue, errorFlag);",
    "    } else if (state.metric === 'thinking') {",
    "      series = buildTimeSeries(entries, flowerOrderOf, thinkingValue, errorFlag, 'Thinking level');",
    "    } else {",
    "      series = buildStackedSeries(entries, flowerOrderOf, COST_COMPONENTS, costValue, errorFlag);",
    "    }",
    "",
    "    return { labels: labels, datasets: series.datasets };",
    "  }",
    "",
    "  var canvas = document.getElementById('gardenChart');",
    "  var chart = new window.Chart(canvas.getContext('2d'), {",
    "    type: 'bar',",
    "    data: { labels: [], datasets: [] },",
    "    options: {",
    "      responsive: true,",
    "      maintainAspectRatio: false,",
    "      animation: { duration: 150 },",
    "      scales: {",
    "        x: {",
    "          stacked: true,",
    "          grid: { color: GRIDLINE_COLOR },",
    "          ticks: { color: TEXT_SECONDARY, autoSkip: true, maxRotation: 45, minRotation: 0 },",
    "        },",
    "        y: {",
    "          stacked: true,",
    "          beginAtZero: true,",
    "          grid: { color: GRIDLINE_COLOR },",
    "          ticks: { color: TEXT_SECONDARY, callback: yAxisFormatter('time') },",
    "        },",
    "      },",
    "      plugins: {",
    "        legend: { display: false },",
    "        tooltip: {",
    "          callbacks: {",
    "            title: function (items) {",
    "              if (!items.length) return '';",
    "              var meta = barMeta[items[0].dataIndex];",
    "              if (!meta) return '';",
    "              if (meta.kind === 'step') return meta.stepLabel + ' · ' + meta.flowerName;",
    "              return meta.flowerName;",
    "            },",
    "            label: function (ctx) {",
    "              var fmt = state.metric === 'time' ? formatDuration",
    "                : state.metric === 'tokens' ? formatNumber",
    "                : state.metric === 'thinking' ? formatThinkingLevel",
    "                : formatCost;",
    "              var line = ctx.dataset.label + ': ' + fmt(ctx.parsed.y);",
    "              if (state.metric === 'tokens' || state.metric === 'price') {",
    "                var meta = barMeta[ctx.dataIndex];",
    "                if (meta) {",
    "                  var total = state.metric === 'tokens'",
    "                    ? formatNumber(meta.tokensTotal)",
    "                    : formatCost(meta.costTotal);",
    "                  line += ' (' + total + ' total)';",
    "                }",
    "              }",
    "              return line;",
    "            },",
    "          },",
    "        },",
    "      },",
    "      onClick: function (evt) {",
    "        var elements = chart.getElementsAtEventForMode(evt, 'nearest', { intersect: true }, true);",
    "        if (!elements.length) return;",
    "        var meta = barMeta[elements[0].index];",
    "        if (!meta) return;",
    "        if (meta.kind === 'step') {",
    "          highlightRows('data-global-order', String(meta.globalOrder));",
    "        } else {",
    "          highlightRows('data-flower-name', meta.flowerName);",
    "        }",
    "      },",
    "    },",
    "  });",
    "",
    "  function updateChart() {",
    "    var cfg = buildChartConfig();",
    "    chart.data.labels = cfg.labels;",
    "    chart.data.datasets = cfg.datasets;",
    "    chart.options.scales.y.ticks.callback = yAxisFormatter(state.metric);",
    "    if (state.metric === 'thinking') {",
    "      chart.options.scales.y.min = 0;",
    "      chart.options.scales.y.max = THINKING_LEVELS.length - 1;",
    "      chart.options.scales.y.ticks.stepSize = 1;",
    "    } else {",
    "      delete chart.options.scales.y.min;",
    "      delete chart.options.scales.y.max;",
    "      delete chart.options.scales.y.ticks.stepSize;",
    "    }",
    "    chart.update();",
    "  }",
    "",
    "  function highlightRows(attr, value) {",
    "    var rows = tbody.querySelectorAll('tr.row-highlight');",
    "    rows.forEach(function (row) { row.classList.remove('row-highlight'); });",
    "    var first = null;",
    "    tbody.querySelectorAll('tr[' + attr + ']').forEach(function (row) {",
    "      if (row.getAttribute(attr) === value) {",
    "        row.classList.add('row-highlight');",
    "        if (!first) first = row;",
    "      }",
    "    });",
    "    if (first) first.scrollIntoView({ behavior: 'smooth', block: 'center' });",
    "  }",
    "",
    "  function wireToggle(role, onChange) {",
    "    var group = document.querySelector('.toggle-group[data-role=\"' + role + '\"]');",
    "    group.querySelectorAll('button').forEach(function (btn) {",
    "      btn.addEventListener('click', function () {",
    "        group.querySelectorAll('button').forEach(function (b) { b.classList.remove('active'); });",
    "        btn.classList.add('active');",
    "        onChange(btn.getAttribute('data-value'));",
    "      });",
    "    });",
    "  }",
    "",
    "  wireToggle('view-mode', function (value) { state.viewMode = value; updateChart(); renderTable(); });",
    "  wireToggle('metric', function (value) { state.metric = value; updateChart(); });",
    "",
    "  updateChart();",
    "})();",
  ].join("\n");
}
